// Push an-/ausschalten, die fuenf Kategorien und die Nachtruhe (Phase 10,
// Projektplan 10.3). Flaeche, die es im Original nicht gibt, deshalb frei
// gestaltet.

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fehlertext } from "../lib/api.js";
import {
  STANDARD_BENACHRICHTIGUNGEN,
  useBenachrichtigungenSpeichern,
  type Benachrichtigungseinstellungen,
} from "../lib/notifications.js";
import { geraetHatAbo, pushAnschalten, pushAusschalten, pushMoeglichImBrowser } from "../lib/push.js";
import { istEigenstaendig, istIOS } from "../lib/ios.js";
import type { Nutzer } from "../lib/me.js";
import { Hinweis, Karte } from "./Bausteine.js";

const PUSH_FEHLER: Record<string, string> = {
  nicht_moeglich: "Push wird von diesem Browser nicht unterstützt.",
  server_aus: "Der Server hat Push noch nicht eingerichtet.",
  abgelehnt: "Die Erlaubnis wurde verweigert. Das lässt sich nur in den Geräteeinstellungen ändern.",
  nicht_erteilt: "Ohne Erlaubnis geht es nicht.",
  kein_sw: "Der Hintergrunddienst der App ist noch nicht bereit. Bitte gleich noch einmal versuchen.",
};

const KATEGORIEN: { schluessel: keyof Benachrichtigungseinstellungen; label: string }[] = [
  { schluessel: "sammlung", label: "Neue Funde in geteilten Sammlungen" },
  { schluessel: "meilensteine", label: "Eigene Meilensteine" },
  { schluessel: "leaderboard", label: "Änderungen im Leaderboard" },
  { schluessel: "system", label: "Wichtige Hinweise zur App" },
  { schluessel: "admin", label: "Neue Freischaltungsanfragen (nur Admin)" },
];

function useGeraeteAbo() {
  return useQuery({ queryKey: ["push-abo"], queryFn: geraetHatAbo, staleTime: 0 });
}

export function PushEinstellungen({ nutzer }: { nutzer: Nutzer }) {
  const queryClient = useQueryClient();
  const abo = useGeraeteAbo();
  const [wirdGeaendert, setWirdGeaendert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [nachtruheAn, setNachtruheAn] = useState(false);
  const benachrichtigungenSpeichern = useBenachrichtigungenSpeichern();

  const einstellungen = {
    ...STANDARD_BENACHRICHTIGUNGEN,
    ...((nutzer.settings as { notifications?: Partial<Benachrichtigungseinstellungen> } | undefined)?.notifications ?? {}),
  };

  // Nur beim ersten Rendern aus den Server-Daten uebernehmen, nicht bei
  // jedem Neuladen: sonst ueberschreibt ein zwischenzeitlich getippter,
  // noch nicht abgeschickter Klick auf die Uhrzeiten sich selbst.
  useEffect(() => {
    setNachtruheAn(Boolean(einstellungen.quietStart && einstellungen.quietEnd));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur beim ersten Rendern, siehe Kommentar oben
  }, []);

  async function umschalten() {
    setFehler(null);
    setWirdGeaendert(true);
    try {
      if (abo.data) {
        await pushAusschalten();
      } else {
        const ergebnis = await pushAnschalten();
        if ("fehler" in ergebnis) {
          setFehler(PUSH_FEHLER[ergebnis.fehler] ?? "Das hat nicht geklappt.");
          return;
        }
      }
      void queryClient.invalidateQueries({ queryKey: ["push-abo"] });
    } finally {
      setWirdGeaendert(false);
    }
  }

  function nachtruheSchalten(an: boolean) {
    setNachtruheAn(an);
    if (!an) benachrichtigungenSpeichern.mutate({ quietStart: null, quietEnd: null });
  }

  const nurInstalliert = istIOS() && !istEigenstaendig();

  return (
    <Karte
      kinder={
        <>
          <h2 className="mb-1 text-base font-medium">Benachrichtigungen</h2>
          <p className="mb-3 text-sm text-neutral-600">
            Eine Meldung, wenn in einer geteilten Sammlung jemand ein neues Kennzeichen einträgt.
          </p>

          {nurInstalliert ? (
            <Hinweis
              art="info"
              kinder='Erst verfügbar, nachdem die App über „Zum Home-Bildschirm hinzufügen" installiert wurde.'
            />
          ) : (
            <>
              {fehler && <Hinweis art="fehler" kinder={fehler} />}
              <label className="mb-3 flex items-center justify-between gap-2 text-sm text-neutral-700">
                Push auf diesem Gerät
                <input
                  type="checkbox"
                  checked={Boolean(abo.data)}
                  disabled={!pushMoeglichImBrowser() || abo.isPending || wirdGeaendert}
                  onChange={() => void umschalten()}
                  className="h-5 w-5"
                />
              </label>
            </>
          )}

          <div className="mt-3 border-t border-neutral-100 pt-3">
            <p className="mb-2 text-sm font-medium text-neutral-700">Wofür</p>
            <ul className="space-y-2">
              {KATEGORIEN.map(({ schluessel, label }) => {
                if (schluessel === "admin" && nutzer.role !== "admin") return null;
                return (
                  <li key={schluessel} className="flex items-center justify-between gap-2 text-sm text-neutral-700">
                    {label}
                    <input
                      type="checkbox"
                      checked={einstellungen[schluessel] as boolean}
                      onChange={(e) => benachrichtigungenSpeichern.mutate({ [schluessel]: e.target.checked })}
                      className="h-5 w-5"
                    />
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mt-3 border-t border-neutral-100 pt-3">
            <label className="flex items-center justify-between gap-2 text-sm text-neutral-700">
              Nachtruhe
              <input
                type="checkbox"
                checked={nachtruheAn}
                onChange={(e) => nachtruheSchalten(e.target.checked)}
                className="h-5 w-5"
              />
            </label>
            {nachtruheAn && (
              <div className="mt-2 flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="time"
                  defaultValue={einstellungen.quietStart ?? "22:00"}
                  onBlur={(e) => benachrichtigungenSpeichern.mutate({ quietStart: e.target.value })}
                  className="min-h-11 rounded border border-neutral-300 px-2"
                />
                <span>bis</span>
                <input
                  type="time"
                  defaultValue={einstellungen.quietEnd ?? "07:00"}
                  onBlur={(e) => benachrichtigungenSpeichern.mutate({ quietEnd: e.target.value })}
                  className="min-h-11 rounded border border-neutral-300 px-2"
                />
              </div>
            )}
          </div>

          {benachrichtigungenSpeichern.error && (
            <Hinweis art="fehler" kinder={fehlertext(benachrichtigungenSpeichern.error)} />
          )}
        </>
      }
    />
  );
}
